import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2,
  Users,
  MapPin,
  Globe,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  BarChart3,
  DollarSign,
  Target,
  Activity,
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  ExternalLink,
  Star,
  Award,
  Briefcase,
  Clock
} from 'lucide-react';
import { Button } from '../../../../components/ui/Button/Button';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { cn } from '../../../../utils/cn';

interface CompanyTabProps {
  campaignData: any;
  onDataChange: (field: string, value: any) => void;
}

interface Company {
  id: string;
  name: string;
  domain: string;
  industry: string;
  size: string;
  location: string;
  revenue: string;
  description: string;
  logo?: string;
  website: string;
  phone?: string;
  email?: string;
  foundedYear: number;
  employees: number;
  technologies: string[];
  socialMedia: {
    linkedin?: string;
    twitter?: string;
  };
  engagement: {
    totalContacts: number;
    emailsSent: number;
    emailsOpened: number;
    emailsReplied: number;
    meetingsBooked: number;
    deals: number;
    revenue: number;
  };
  lastActivity: string;
  status: 'prospect' | 'engaged' | 'qualified' | 'customer' | 'churned';
}

const mockCompanies: Company[] = [
  {
    id: '1',
    name: 'TechCorp Solutions',
    domain: 'techcorp.com',
    industry: 'Software',
    size: '500-1000',
    location: 'San Francisco, CA',
    revenue: '$50M-100M',
    description: 'Leading provider of enterprise software solutions for digital transformation.',
    website: 'https://techcorp.com',
    foundedYear: 2015,
    employees: 750,
    technologies: ['React', 'Node.js', 'AWS', 'Docker'],
    socialMedia: {
      linkedin: 'https://linkedin.com/company/techcorp',
      twitter: 'https://twitter.com/techcorp'
    },
    engagement: {
      totalContacts: 25,
      emailsSent: 150,
      emailsOpened: 105,
      emailsReplied: 18,
      meetingsBooked: 5,
      deals: 2,
      revenue: 125000
    },
    lastActivity: '2 hours ago',
    status: 'qualified'
  },
  {
    id: '2',
    name: 'Global Dynamics Inc',
    domain: 'globaldynamics.com',
    industry: 'Manufacturing',
    size: '1000+',
    location: 'Chicago, IL',
    revenue: '$100M+',
    description: 'International manufacturing company specializing in industrial automation.',
    website: 'https://globaldynamics.com',
    foundedYear: 1998,
    employees: 2500,
    technologies: ['SAP', 'Oracle', 'Salesforce'],
    socialMedia: {
      linkedin: 'https://linkedin.com/company/globaldynamics'
    },
    engagement: {
      totalContacts: 45,
      emailsSent: 280,
      emailsOpened: 196,
      emailsReplied: 32,
      meetingsBooked: 8,
      deals: 1,
      revenue: 250000
    },
    lastActivity: '1 day ago',
    status: 'engaged'
  }
];

const industryColors = {
  'Software': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'Manufacturing': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  'Healthcare': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'Finance': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  'Retail': 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  'Education': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
};

const statusColors = {
  'prospect': 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  'engaged': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'qualified': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'customer': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  'churned': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export const CompanyTab: React.FC<CompanyTabProps> = ({ campaignData, onDataChange }) => {
  const [activeView, setActiveView] = useState<'overview' | 'companies' | 'insights' | 'analytics'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Total Companies</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">1,247</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+12%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Qualified Leads</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">89</p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+28%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Active Deals</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">23</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+15%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Pipeline Value</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">$2.4M</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+22%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>
      </div>

      {/* Top Performing Companies */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Top Performing Companies</h3>
          <Button variant="outline" size="sm" onClick={() => setActiveView('companies')}>
            View All
          </Button>
        </div>
        <div className="space-y-4">
          {mockCompanies.slice(0, 3).map((company) => (
            <div key={company.id} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white dark:bg-neutral-700 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-neutral-600 dark:text-neutral-400" />
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900 dark:text-neutral-100">{company.name}</h4>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">{company.industry} • {company.size} employees</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {formatCurrency(company.engagement.revenue)}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    {company.engagement.deals} deals
                  </p>
                </div>
                <span className={cn(
                  "px-2 py-1 rounded-full text-xs font-medium",
                  statusColors[company.status]
                )}>
                  {company.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderCompanies = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">All Companies</h3>
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Button variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Company
          </Button>
        </div>
      </div>

      <div className="grid gap-6">
        {mockCompanies.map((company) => (
          <Card key={company.id} className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-neutral-600 dark:text-neutral-400" />
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">{company.name}</h4>
                    <Button variant="ghost" size="sm">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-2">{company.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-neutral-600 dark:text-neutral-400">
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {company.location}
                    </span>
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {company.employees.toLocaleString()} employees
                    </span>
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      Founded {company.foundedYear}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={cn(
                  "px-3 py-1 rounded-full text-sm font-medium",
                  industryColors[company.industry as keyof typeof industryColors] || industryColors.Software
                )}>
                  {company.industry}
                </span>
                <span className={cn(
                  "px-3 py-1 rounded-full text-sm font-medium",
                  statusColors[company.status]
                )}>
                  {company.status}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-6 mb-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{company.engagement.totalContacts}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Contacts</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{company.engagement.emailsSent}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Emails Sent</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {((company.engagement.emailsOpened / company.engagement.emailsSent) * 100).toFixed(1)}%
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Open Rate</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{company.engagement.meetingsBooked}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Meetings</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{company.engagement.deals}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Deals</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatCurrency(company.engagement.revenue)}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Revenue</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-neutral-400" />
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Last activity: {company.lastActivity}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm" onClick={() => setSelectedCompany(company)}>
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </Button>
                <Button variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderInsights = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Company Insights</h3>
      
      <div className="grid grid-cols-2 gap-6">
        <Card className="p-6">
          <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Industry Distribution</h4>
          <div className="space-y-3">
            {Object.entries(industryColors).map(([industry, colorClass]) => (
              <div key={industry} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={cn("w-3 h-3 rounded-full", colorClass.split(' ')[0])} />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">{industry}</span>
                </div>
                <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  {Math.floor(Math.random() * 30 + 10)}%
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Company Size Distribution</h4>
          <div className="space-y-3">
            {['1-50', '51-200', '201-500', '501-1000', '1000+'].map((size) => (
              <div key={size} className="flex items-center justify-between">
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{size} employees</span>
                <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  {Math.floor(Math.random() * 25 + 15)}%
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Engagement Performance by Industry</h4>
        <div className="space-y-4">
          {Object.keys(industryColors).map((industry) => (
            <div key={industry} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className={cn(
                  "px-2 py-1 rounded text-xs font-medium",
                  industryColors[industry as keyof typeof industryColors]
                )}>
                  {industry}
                </span>
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <p className="font-medium text-neutral-900 dark:text-neutral-100">{Math.floor(Math.random() * 50 + 30)}%</p>
                  <p className="text-neutral-600 dark:text-neutral-400">Open Rate</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-neutral-900 dark:text-neutral-100">{Math.floor(Math.random() * 15 + 5)}%</p>
                  <p className="text-neutral-600 dark:text-neutral-400">Reply Rate</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-neutral-900 dark:text-neutral-100">{formatCurrency(Math.floor(Math.random() * 100000 + 50000))}</p>
                  <p className="text-neutral-600 dark:text-neutral-400">Avg Deal Size</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Company Analytics</h3>
      
      <div className="grid grid-cols-2 gap-6">
        <Card className="p-6">
          <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Revenue by Company Size</h4>
          <div className="space-y-4">
            {['Enterprise (1000+)', 'Mid-Market (201-1000)', 'SMB (1-200)'].map((segment, index) => (
              <div key={segment} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-900 dark:text-neutral-100">{segment}</span>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">
                    {formatCurrency([850000, 420000, 180000][index])}
                  </span>
                </div>
                <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full"
                    style={{ width: `${[85, 42, 18][index]}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Conversion Funnel</h4>
          <div className="space-y-4">
            {[
              { stage: 'Total Companies', count: 1247, percentage: 100 },
              { stage: 'Contacted', count: 892, percentage: 71.5 },
              { stage: 'Engaged', count: 234, percentage: 18.8 },
              { stage: 'Qualified', count: 89, percentage: 7.1 },
              { stage: 'Customers', count: 23, percentage: 1.8 }
            ].map((item) => (
              <div key={item.stage} className="flex items-center justify-between">
                <span className="text-sm text-neutral-900 dark:text-neutral-100">{item.stage}</span>
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{item.count}</span>
                  <span className="text-xs text-neutral-600 dark:text-neutral-400">({item.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex items-center space-x-1 bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'companies', label: 'Companies', icon: Building2 },
          { id: 'insights', label: 'Insights', icon: TrendingUp },
          { id: 'analytics', label: 'Analytics', icon: Activity }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as any)}
              className={cn(
                "flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
                activeView === tab.id
                  ? "bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <motion.div
        key={activeView}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeView === 'overview' && renderOverview()}
        {activeView === 'companies' && renderCompanies()}
        {activeView === 'insights' && renderInsights()}
        {activeView === 'analytics' && renderAnalytics()}
      </motion.div>
    </div>
  );
}; 